import { Shield } from 'lucide-react'
import { formatBytes } from '@/utils/format'

interface UploadProgressProps {
  files: File[]
  progress: number
  phase?: 'encrypting' | 'uploading'
  onCancel?: () => void
}

export function UploadProgress({ files, progress, phase = 'uploading', onCancel }: UploadProgressProps) {
  const totalSize = files.reduce((acc, file) => acc + file.size, 0)
  const isEncrypting = phase === 'encrypting'

  return (
    <div className="card p-8 text-center">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isEncrypting ? 'bg-green-100' : 'bg-primary-100'}`}>
        {isEncrypting ? (
          <Shield className="w-8 h-8 text-green-600 animate-pulse" />
        ) : (
          <svg className="w-8 h-8 text-primary-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        )}
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        {isEncrypting ? 'Encrypting files...' : 'Uploading files...'}
      </h2>
      <p className="text-gray-500 mb-6">
        {files.length} file{files.length > 1 ? 's' : ''} ({formatBytes(totalSize)})
      </p>

      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${isEncrypting ? 'bg-green-500' : 'bg-primary-600'}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="text-sm text-gray-500 mb-4">{progress}% complete</p>

      {onCancel && (
        <button
          onClick={onCancel}
          className="btn-secondary btn-md"
        >
          Cancel
        </button>
      )}
    </div>
  )
}
